import {captureException} from '@sentry/browser';
import {showNotification} from 'platform/components';
import type {z} from 'zod';

import {useRef} from 'react';

import {
  newVehicleApi,
  useCreatePackageMutation,
  useUpdatePackageMutation,
  type FeatureResponseBody,
  type PackageResponseBody,
  type PackageWriteRequestBody,
  type TrimResponseBody,
} from '@dms/api/new-vehicle';

import type {packageSchema} from '../schemas/packageSchema';
import type {PricedItemChangeOutcome} from '../types/PricedItemChangeOutcome';
import {
  catalogueErrorCodes,
  catalogueErrorMessage,
  handleCatalogueError,
  t,
} from '../utils/catalogueUi';
import {isApiError} from '../utils/isApiError';
import {FEATURE_OPTIONAL_ON_TRIM} from '../utils/isCodedEntryRefusal';
import {isInPricelist} from '../utils/isInPricelist';
import {offeredTrimChanges} from '../utils/offeredTrimChanges';
import {toOfferedTrims} from '../utils/offeredTrimRows';
import {packageWriteEntry} from '../utils/packageWriteEntry';
import {usePackageTaxonomySave} from './usePackageTaxonomySave';
import type {usePublishedPriceSave} from './usePublishedPriceSave';

type PackageValues = z.infer<typeof packageSchema>;
/** The package as the last successful write left it on the server. */
export type PackageCheckpoint = Pick<
  PackageResponseBody,
  'id' | 'version' | 'name' | 'status' | 'trims'
>;

/**
 * The package builder's write: the package, then the library taxonomy of its members. A package
 * write that succeeds is checkpointed, so a retry after a failed taxonomy save updates the package
 * instead of creating it again, and sends no pricelist version that the server already applied.
 */
export function usePackageSave(props: {
  modelYearId: string;
  /** The package as last read; undefined for a new one. */
  item?: PackageResponseBody;
  features: FeatureResponseBody[];
  /** The trims the package can be offered on: the stored ones and the ones the open group creates. */
  trims: Pick<TrimResponseBody, 'id' | 'name' | 'publicationStatus'>[];
  /** The trims of `trims` that a create of the open group makes. */
  groupedTrimIds: string[];
  canViewCost: boolean;
  publishedPrice: Pick<ReturnType<typeof usePublishedPriceSave>, 'writePricedItemChange'>;
}) {
  const [create] = useCreatePackageMutation();
  const [update] = useUpdatePackageMutation();
  const [getPackage] = newVehicleApi.useLazyGetPackageQuery();
  const [getFeature] = newVehicleApi.useLazyGetFeatureQuery();
  const {saveTaxonomy, taxonomyUpdates} = usePackageTaxonomySave(props.features);
  const persisted = useRef<PackageCheckpoint | null>(null);
  /** The label of the pricelist version that the package write of the current save created. */
  const createdVersionLabel = useRef<string | null>(null);

  /**
   * A published year asks for a new pricelist version when the write puts trim rows into the dealer
   * price list or takes them out (D102): a new active package, a trim added or dropped, or a switch
   * between draft and active. The taxonomy saves inside the write, so a failed one after a version
   * still names that version (reportFailure). With an open group the package write joins it with
   * the feature writes of its taxonomy (Q8), and can offer the package on a trim the group creates.
   */
  const save = (values: PackageValues): Promise<PricedItemChangeOutcome> => {
    createdVersionLabel.current = null;
    const stored = persisted.current ?? props.item;
    // A grouped new trim picked here that left the group since is not sent: no write creates it.
    const offerable = values.trims.filter(({trimId}) => props.trims.some(({id}) => id === trimId));
    const trims = toOfferedTrims(offerable, stored?.trims ?? [], props.canViewCost);
    const body: PackageWriteRequestBody = {
      modelYearId: props.modelYearId,
      name: values.name,
      codes: values.factoryCode
        ? [values.factoryCode, ...(props.item?.codes.slice(1) ?? [])]
        : (props.item?.codes.slice(1) ?? []),
      description: values.description,
      memberFeatureIds: values.memberFeatureIds,
      trims,
      status: values.status,
      version: stored?.version ?? 1,
    };
    const persist = ({id, version, name, status, trims: saved}: PackageResponseBody) => {
      persisted.current = {id, version, name, status, trims: saved};
    };
    const items = offeredTrimChanges({
      resourceType: 'packageTrim',
      trims: props.trims,
      stored: {
        name: stored?.name ?? values.name,
        rows: stored?.trims ?? [],
        isMember: isInPricelist.package(stored?.status ?? 'draft'),
      },
      next: {name: values.name, rows: trims, isMember: isInPricelist.package(values.status)},
    });
    return props.publishedPrice.writePricedItemChange({
      items,
      namesGroupedItem: trims.some(({trimId}) => props.groupedTrimIds.includes(trimId)),
      entry: packageWriteEntry({
        body,
        items,
        stored,
        readVersion: (packageId) =>
          getPackage({packageId}, false)
            .unwrap()
            .then(({version}) => version),
        taxonomy: values,
        changedFeatureIds: taxonomyUpdates(values).map(({featureId}) => featureId),
        readFeature: (featureId) => getFeature({featureId}, false).unwrap(),
        groupedTrimIds: props.groupedTrimIds,
      }),
      write: (pricelistVersion) => {
        const packageWriteRequestBody = {...body, ...(pricelistVersion && {pricelistVersion})};
        return (
          stored
            ? update({packageId: stored.id, packageWriteRequestBody})
            : create({packageWriteRequestBody})
        )
          .unwrap()
          .then((saved) => {
            persist(saved);
            createdVersionLabel.current = pricelistVersion?.label ?? null;
          })
          .then(() => saveTaxonomy(values));
      },
    });
  };

  /**
   * Reports a failed save. After a package write that succeeded (the checkpoint changed), only the
   * taxonomy is missing: the feedback says so, and names the pricelist version the write created,
   * because the version dialog closed on the failure without saying it. A member feature that the
   * BE refuses (CT-0079) shows the BE reason instead of the retry text.
   */
  const reportFailure = (error: unknown, checkpoint: PackageCheckpoint | null) => {
    if (persisted.current === checkpoint) {
      handleCatalogueError(error);
      return;
    }
    if (!isApiError(error)) {
      captureException(error);
    }
    if (createdVersionLabel.current !== null) {
      showNotification.success(
        t('entity.newVehicle.catalogue.itemChange.applied', 'Saved. Pricelist is now {label}.', {
          label: createdVersionLabel.current,
        })
      );
    }
    // A refused member feature fails again on a retry: the reason of the BE replaces the retry text.
    if (catalogueErrorCodes(error).includes(FEATURE_OPTIONAL_ON_TRIM)) {
      showNotification.error(
        t(
          'entity.newVehicle.catalogue.packageSavedFeatureRefused',
          'The package was saved, but this equipment setting was not: {reason}',
          {reason: catalogueErrorMessage(error)}
        ),
        {errorOrigin: 'business'}
      );
      return;
    }
    showNotification.error(
      t(
        'entity.newVehicle.catalogue.packageSavedFeatureFailed',
        'The package was saved, but some equipment settings were not. Retry to finish saving — the package will not be created again.'
      )
    );
  };

  return {persisted, save, reportFailure};
}
